import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { FunnelStage, CAMPAIGN_TYPE, getCampaignType } from './types';
import type { CampaignData, CampaignTabValue, SettingsTabValue, TasksTabValue, TaskTrackingSummary } from './types';
import { RECOMMENDATION_STATE, UserRole } from './types';
import i18n from '@/i18n';

// Emoji detection regex - matches Unicode emoji ranges
// This pattern correctly identifies emojis without false positives for regular punctuation like dashes
// Covers: Miscellaneous Symbols, Dingbats, Emoticons, Transport, Symbols, Flags, and other emoji ranges
const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{200D}]|[\u{FE0F}]|[\u{1F018}-\u{1F270}]/u;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isResolvedState(state: RECOMMENDATION_STATE) {
  return state === RECOMMENDATION_STATE.INSTANT_FIX_APPLIED || state === RECOMMENDATION_STATE.MARKED_RESOLVED;
}

/**
 * Safely get funnel stage from ad_funnel_stage object
 */
export function getFunnelStage(adFunnelStage: { funnel?: FunnelStage } | null | undefined): FunnelStage {
  if (!adFunnelStage) return FunnelStage.UPPER_FUNNEL;
  if (!adFunnelStage.funnel) return FunnelStage.UPPER_FUNNEL;

  // If it's already a FunnelStage enum value, return it
  if (Object.values(FunnelStage).includes(adFunnelStage.funnel as FunnelStage)) {
    return adFunnelStage.funnel as FunnelStage;
  }

  // Otherwise return the default
  return FunnelStage.UPPER_FUNNEL;
}

/**
 * Sort key for funnel stages. Top → Middle → Bottom of Funnel.
 * Use as: `funnelStages.sort((a, b) => getFunnelStageOrder(a.funnel) - getFunnelStageOrder(b.funnel))`.
 * Unknown stages sort to the end of the list.
 */
export function getFunnelStageOrder(stage: string | null | undefined): number {
  switch (stage) {
    case FunnelStage.UPPER_FUNNEL:
      return 0;
    case FunnelStage.MIDDLE_FUNNEL:
      return 1;
    case FunnelStage.LOWER_FUNNEL:
      return 2;
    default:
      return 99;
  }
}

/**
 * Get display label for a funnel stage using Top/Middle/Bottom naming
 */
export function getFunnelStageLabel(stage: string): string {
  switch (stage) {
    case FunnelStage.UPPER_FUNNEL:
      return 'Top of Funnel';
    case FunnelStage.MIDDLE_FUNNEL:
      return 'Middle of Funnel';
    case FunnelStage.LOWER_FUNNEL:
      return 'Bottom of Funnel';
    default:
      return stage.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

export const getGoogleAdsCampaignLink = (campaign: CampaignData) => {
  if (
    campaign.googleads_campaign &&
    campaign.googleads_account.googleads_account_id &&
    campaign.googleads_campaign.googleads_campaign_id
  ) {
    const x = `https://ads.google.com/aw/campaigns?campaignId=${campaign.googleads_campaign.googleads_campaign_id}&customerId=${campaign.googleads_account_id}`;
    return x;
  }
  return null;
};

export function setCookie(name: string, value: string, days: number) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  // SameSite=Lax always (CSRF posture); Secure only over HTTPS so local
  // http dev keeps working. Client-set cookies can never be HttpOnly — the
  // long-term fix is a backend-set session cookie (#4872).
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = name + '=' + (value || '') + expires + '; path=/; SameSite=Lax' + secure;
}

export function debounce<Params extends unknown[]>(
  func: (...args: Params) => unknown,
  timeout: number,
): (...args: Params) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Params) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, timeout);
  };
}

export function isValidUrl(url: string) {
  const res = url.match(
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g,
  );
  if (res == null) return false;
  else return true;
}

export function isValidSegment(segment: string): boolean {
  return /^[a-zA-Z0-9-_]+$/.test(segment);
}

export function isValidInput(input: string): boolean {
  return /^[a-zA-Z0-9-_\s]+$/.test(input);
}

export function isAdmin(role: UserRole) {
  return role === UserRole.ADMIN;
}

export function removeEmojis(input: string): string {
  // Use a regular expression to match and remove emojis
  return input.replace(emojiRegex, '');
}

export function containsEmoji(input: string): boolean {
  // Check if any emoji is present in the string
  return emojiRegex.test(input);
}

export function isCampaignTabValue(value: string): value is CampaignTabValue {
  return ['overview', 'ads', 'assets', 'settings'].includes(value);
}

export function isSettingsTabValue(value: string): value is SettingsTabValue {
  return ['company', 'team', 'billing', 'notification', 'privacy', 'integrations', 'reports'].includes(value);
}

export function isTasksTabValue(value: string): value is TasksTabValue {
  return ['new', 'logs', 'recommendations'].includes(value);
}

export function getInitialUserName(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()[0];
}

export const formatPhoneNumber = (value: string): string => {
  let cleaned = value.replace(/[^\d+]/g, '');

  cleaned = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;

  const formatted = cleaned.replace(
    /^(\+\d{0,1})(\d{0,3})(\d{0,2})(\d{0,3}).*/,
    (_, p1 = '+', p2 = '', p3 = '', p4 = '') => {
      const parts = [p1];
      if (p2) parts.push(p2);
      if (p3) parts.push(p3);
      if (p4) parts.push(p4);
      return parts.join(' ').trim();
    },
  );

  return formatted;
};

export const validatePhone = (input: string): boolean => {
  return /^\+(?=.*\d)[+\d\s]*$/.test(input);
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const VOID = () => {
  /** */
};

/**
 * Deprecated: Use getCampaignType() and enum comparison instead
 */
export function isBrandCampaign(campaign: { goal?: string } | null): boolean {
  if (!campaign) return false;
  const campaignType = getCampaignType(campaign);
  return campaignType === CAMPAIGN_TYPE.BRAND;
}

/**
 * New general campaign type checking function
 */
export function isCampaignType(campaign: { goal?: string } | null, type: CAMPAIGN_TYPE): boolean {
  if (!campaign) return false;
  const campaignType = getCampaignType(campaign);
  return campaignType === type;
}

export function isTaskTrackingSummary(taskTrackingSummary: unknown): taskTrackingSummary is TaskTrackingSummary {
  if (typeof taskTrackingSummary !== 'object' || taskTrackingSummary === null) {
    return false;
  }

  const obj = taskTrackingSummary as Record<string, unknown>;

  return (
    'total' in obj &&
    'in_progress' in obj &&
    typeof obj.in_progress === 'number' &&
    'completed' in obj &&
    typeof obj.completed === 'number' &&
    'failed' in obj &&
    typeof obj.failed === 'number'
  );
}

/**
 * Formats a keyword for display by removing match type convention characters.
 *
 * Match type conventions:
 * - [keyword] = EXACT match (brand campaigns) - brackets stripped for display
 * - "keyword" = PHRASE match (competitor campaigns) - quotes stripped for display
 * - keyword = BROAD match (default) - displayed as-is
 *
 * This function only affects display - it doesn't modify the actual data.
 */
export function formatKeywordForDisplay(keywordText: string): string {
  // EXACT match: strip brackets [keyword] -> keyword
  if (keywordText.startsWith('[') && keywordText.endsWith(']')) {
    return keywordText.substring(1, keywordText.length - 1);
  }
  // PHRASE match: strip quotes "keyword" -> keyword
  if (keywordText.startsWith('"') && keywordText.endsWith('"')) {
    return keywordText.substring(1, keywordText.length - 1);
  }
  return keywordText;
}

// #4810: location names (e.g. canonical_name) arrive comma-separated without a space, like
// "Northern Ireland,United Kingdom". Add a space after each comma for display only — callers keep
// the raw value for matching/keys. The lookahead skips a following space so it's idempotent.
export function formatLocationName(name: string): string {
  return name.replace(/,(?=\S)/g, ', ');
}

/**
 * Compact reach estimate for the location tables (e.g. 4_900_000 -> "4.9M").
 * Returns "-" when reach is unknown — radius targets have no catalog reach, and
 * not every geo-target carries one.
 */
export function formatReach(n: number | null | undefined): string {
  return n == null
    ? '-'
    : new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

// Format task content for display
export function formatTaskContent(action: { action_type: string; details?: { count?: number } }): string {
  if (!action) return '';
  
  switch(action.action_type) {
    case 'add_keyword':
      return `Added keyword${(action.details?.count ?? 0) > 1 ? 's' : ''}`;
    case 'delete_keyword':
      return `Removed keyword${(action.details?.count ?? 0) > 1 ? 's' : ''}`;
    case 'pause_keyword':
      return `Paused keyword${(action.details?.count ?? 0) > 1 ? 's' : ''}`;
    case 'add_negative_keyword':
    case 'add_negative_keywords':
      return `Added negative keyword${(action.details?.count ?? 0) > 1 ? 's' : ''}`;
    case 'delete_zero_impression_keywords':
      return `Removed zero impression keywords`;
    case 'add_replacement_keywords':
      return `Added replacement keywords`;
    default:
      // Transform action_type from snake_case to Title Case (capitalize first letter of each word)
      return action.action_type
        .replace(/_/g, ' ')
        .replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
  }
}

// Format keyword details for display in task detail modal
export function getKeywordsFromDetails(details: { keywords?: string[] } | null | undefined): string[] {
  if (!details) return [];
  
  // Check if we have keywords array
  if (details.keywords && Array.isArray(details.keywords)) {
    return details.keywords.map((keyword: string) => formatKeywordForDisplay(keyword));
  }
  
  return [];
}

// Format date for display
export function formatTaskDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(i18n.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// List of generic email providers that shouldn't be used for business pre-population
const GENERIC_EMAIL_PROVIDERS = [
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'yahoo.co.uk',
  'ymail.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'protonmail.com',
  'proton.me',
  'zoho.com',
  'mail.com',
  'gmx.com',
  'gmx.net',
  'fastmail.com',
  'tutanota.com',
  'yandex.com',
  'qq.com',
  '163.com',
  '126.com',
];

/**
 * Checks if an email domain is a generic provider (Gmail, Hotmail, etc.)
 */
export function isGenericEmailProvider(email: string): boolean {
  if (!email || !email.includes('@')) return true;
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return true;
  return GENERIC_EMAIL_PROVIDERS.includes(domain);
}

/**
 * Extracts domain from email address
 */
export function getEmailDomain(email: string): string | null {
  if (!email || !email.includes('@')) return null;
  return email.split('@')[1]?.toLowerCase() || null;
}

/**
 * Generates a reasonable business name from email domain
 * e.g., "acme-corp.com" -> "Acme Corp"
 */
export function getBusinessNameFromDomain(domain: string): string {
  // Remove TLD (.com, .co.uk, etc.)
  const namePart = domain.split('.')[0];
  // Replace hyphens and underscores with spaces, capitalize each word
  return namePart
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Generates a website URL from email domain
 * e.g., "acme.com" -> "https://www.acme.com"
 */
export function getWebsiteFromDomain(domain: string): string {
  return `https://www.${domain}`;
}

/**
 * Sanitize a generated business description:
 * - Replace em dashes (—) and en dashes (–) with colons or commas
 * - Ensure paragraph breaks between sections (double newlines)
 */
export function formatBusinessDescription(text: string): string {
  if (!text) return text;
  // Replace em/en dashes surrounded by spaces with a colon
  let result = text.replace(/\s[—–]\s/g, ': ');
  // Replace em/en dashes at start of line (list-style) with a hyphen
  result = result.replace(/^[—–]\s/gm, '- ');
  // Replace any remaining em/en dashes with a comma
  result = result.replace(/[—–]/g, ', ');
  // Normalize line breaks: ensure double newlines between paragraphs
  // Collapse 3+ newlines to 2, but preserve existing double newlines
  result = result.replace(/\n{3,}/g, '\n\n');
  return result.trim();
}
